/**
 * CinematicPipeline — Triple-AAA post-process stack in a single pass.
 *
 * Phaser 4 architecture: a paired (RenderNode, Controller) filter where
 *   - {@link CinematicShader} is the WebGL render node (owns the fragment shader)
 *   - {@link CinematicFilter} is the camera-attached controller (owns settings + API)
 *
 * The fragment shader is identical to the v3 implementation so visual output
 * is preserved bit-for-bit. The migration is purely architectural: v3's
 * `Phaser.Renderer.WebGL.Pipelines.PostFXPipeline` is gone in v4 and was
 * replaced with the RenderNode + Filter pair pattern.
 *
 * One fragment shader does the whole Overwatch-grade finishing pass so we only
 * pay one full-screen sample cost (instead of 4-5 separate filters):
 *
 *   1. Selective bloom (bright-pass + 9-tap blur approximation)
 *   2. Radial vignette with danger-pulse channel
 *   3. Chromatic aberration (RGB split at frame corners, intensifies on damage)
 *   4. Film grain (animated time-seeded noise)
 *   5. Subtle scanline / VHS shimmer on HIGH combo
 *   6. Color grade (lift shadows cool, crush highlights warm)
 *
 * Exposed controls (per-scene, on the controller):
 *   filter.setBloom(intensity, threshold)
 *   filter.setVignette(strength)
 *   filter.setChromatic(strength)
 *   filter.setGrain(strength)
 *   filter.setComboShimmer(intensity)
 *   filter.setDangerPulse(0..1)
 *   filter.applyPreset('gameplay' | 'boss' | 'menu' | 'stageClear' | 'gameOver')
 *
 * Attach with:
 *   const filter = CinematicFilter.attachTo(this.cameras.main);
 *   filter.applyPreset('gameplay');
 *
 * Registration (once, in main.ts game config):
 *   render: { renderNodes: { CinematicShader: { function: CinematicShader } } }
 */

import Phaser from 'phaser';

const CINEMATIC_FRAG = `
#ifdef GL_ES
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform float uTime;

uniform float uBloomIntensity;
uniform float uBloomThreshold;
uniform float uVignetteStrength;
uniform float uChromaticStrength;
uniform float uGrainStrength;
uniform float uComboShimmer;
uniform float uDangerPulse;
uniform float uWarm;
uniform float uCool;
uniform float uSaturation;

varying vec2 outTexCoord;

// Hash-based white noise, time-seeded
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

// Cheap 9-tap blur used for the bloom pass
vec3 blur9(sampler2D tex, vec2 uv, vec2 px) {
  vec3 col = vec3(0.0);
  col += texture2D(tex, uv + px * vec2(-1.0, -1.0)).rgb * 0.0625;
  col += texture2D(tex, uv + px * vec2( 0.0, -1.0)).rgb * 0.125;
  col += texture2D(tex, uv + px * vec2( 1.0, -1.0)).rgb * 0.0625;
  col += texture2D(tex, uv + px * vec2(-1.0,  0.0)).rgb * 0.125;
  col += texture2D(tex, uv + px * vec2( 0.0,  0.0)).rgb * 0.25;
  col += texture2D(tex, uv + px * vec2( 1.0,  0.0)).rgb * 0.125;
  col += texture2D(tex, uv + px * vec2(-1.0,  1.0)).rgb * 0.0625;
  col += texture2D(tex, uv + px * vec2( 0.0,  1.0)).rgb * 0.125;
  col += texture2D(tex, uv + px * vec2( 1.0,  1.0)).rgb * 0.0625;
  return col;
}

void main() {
  vec2 uv = outTexCoord;
  vec2 center = vec2(0.5, 0.5);
  vec2 toCenter = uv - center;
  float distFromCenter = length(toCenter);

  // CHROMATIC ABERRATION (stronger at corners, pulses on danger)
  float chromaAmount = uChromaticStrength + uDangerPulse * 4.0 + uComboShimmer * 2.0;
  float edgeFactor = smoothstep(0.15, 0.75, distFromCenter);
  vec2 chromaOffset = normalize(toCenter + vec2(0.001)) * chromaAmount * edgeFactor / uResolution;

  vec3 color;
  color.r = texture2D(uMainSampler, uv + chromaOffset).r;
  color.g = texture2D(uMainSampler, uv).g;
  color.b = texture2D(uMainSampler, uv - chromaOffset).b;

  // BLOOM — bright-pass on a blurred sample
  vec2 pxSize = vec2(2.5) / uResolution;
  vec3 blurred = blur9(uMainSampler, uv, pxSize);
  vec3 bright = max(blurred - vec3(uBloomThreshold), vec3(0.0));
  bright *= bright * (1.5 + uComboShimmer * 2.0);
  color += bright * uBloomIntensity;

  // COLOR GRADE — gentle lift; keeps sprite colors readable
  float luma = dot(color, vec3(0.299, 0.587, 0.114));
  vec3 cool = vec3(0.94, 0.98, 1.02);
  vec3 warm = vec3(1.03, 0.99, 0.96);
  color = mix(color * cool, color * warm, smoothstep(0.25, 0.85, luma));
  color = mix(color, color * warm, uWarm * smoothstep(0.5, 1.0, luma));
  color = mix(color, color * cool, uCool * (1.0 - smoothstep(0.0, 0.3, luma)));

  // SATURATION BOOST
  float sLuma = dot(color, vec3(0.299, 0.587, 0.114));
  color = mix(vec3(sLuma), color, uSaturation);

  // VIGNETTE (radial darkening + optional crimson danger pulse)
  float vig = smoothstep(0.35, 0.92, distFromCenter);
  vec3 vigColor = mix(vec3(0.0, 0.0, 0.02), vec3(0.35, 0.02, 0.04), uDangerPulse);
  color = mix(color, vigColor, vig * uVignetteStrength);

  // SCANLINE / COMBO SHIMMER
  float scan = sin(uv.y * uResolution.y * 2.0 + uTime * 8.0) * 0.5 + 0.5;
  color += vec3(0.35, 0.7, 1.0) * scan * 0.02 * uComboShimmer;

  // FILM GRAIN
  float grain = hash(uv * uResolution + vec2(uTime * 60.0, uTime * 120.0));
  color += (grain - 0.5) * uGrainStrength;

  gl_FragColor = vec4(clamp(color, 0.0, 1.2), 1.0);
}
`;

/**
 * Render node — owns the WebGL shader and per-frame uniform plumbing.
 * Register once via `config.render.renderNodes` in main.ts.
 */
export class CinematicShader extends Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader {
  constructor(manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager) {
    super('CinematicShader', manager, undefined, CINEMATIC_FRAG);
  }

  setupUniforms(
    controller: Phaser.Filters.Controller,
    drawingContext: Phaser.Renderer.WebGL.DrawingContext,
  ): void {
    const c = controller as CinematicFilter;
    const pm = this.programManager;
    pm.setUniform('uResolution', [drawingContext.width, drawingContext.height]);
    pm.setUniform('uTime', c.camera.scene.game.loop.time / 1000);
    pm.setUniform('uBloomIntensity', c.bloomIntensity);
    pm.setUniform('uBloomThreshold', c.bloomThreshold);
    pm.setUniform('uVignetteStrength', c.vignetteStrength);
    pm.setUniform('uChromaticStrength', c.chromaticStrength);
    pm.setUniform('uGrainStrength', c.grainStrength);
    pm.setUniform('uComboShimmer', c.comboShimmer);
    pm.setUniform('uDangerPulse', c.dangerPulse);
    pm.setUniform('uWarm', c.colorGradeWarm);
    pm.setUniform('uCool', c.colorGradeCool);
    pm.setUniform('uSaturation', c.saturation);
  }
}

export type CinematicPreset = 'gameplay' | 'boss' | 'menu' | 'stageClear' | 'gameOver';

/**
 * Camera-attached controller — the public, mutable settings surface.
 * Same setter API as the v3 pipeline so all callers stay unchanged.
 */
export class CinematicFilter extends Phaser.Filters.Controller {
  bloomIntensity = 0.45;
  bloomThreshold = 0.75;
  vignetteStrength = 0.28;
  chromaticStrength = 0.3;
  grainStrength = 0.02;
  comboShimmer = 0.0;
  dangerPulse = 0.0;
  colorGradeWarm = 0.08;
  colorGradeCool = 0.05;
  saturation = 1.18;

  constructor(camera: Phaser.Cameras.Scene2D.Camera) {
    super(camera, 'CinematicShader');
  }

  /** Construct + add to the camera's internal filter list. */
  static attachTo(cam: Phaser.Cameras.Scene2D.Camera): CinematicFilter {
    const filter = new CinematicFilter(cam);
    cam.filters.internal.add(filter);
    return filter;
  }

  setBloom(intensity: number, threshold?: number): this {
    this.bloomIntensity = intensity;
    if (threshold !== undefined) this.bloomThreshold = threshold;
    return this;
  }
  setVignette(strength: number): this {
    this.vignetteStrength = strength;
    return this;
  }
  setChromatic(strength: number): this {
    this.chromaticStrength = strength;
    return this;
  }
  setGrain(strength: number): this {
    this.grainStrength = strength;
    return this;
  }
  /** 0..1 — ramps up scanline + chromatic + bloom boost during high combos. */
  setComboShimmer(v: number): this {
    this.comboShimmer = Math.max(0, Math.min(1, v));
    return this;
  }
  /** 0..1 — tints vignette red & intensifies chromatic for low-health / boss enrage. */
  setDangerPulse(v: number): this {
    this.dangerPulse = Math.max(0, Math.min(1, v));
    return this;
  }
  setColorGrade(warm: number, cool: number): this {
    this.colorGradeWarm = warm;
    this.colorGradeCool = cool;
    return this;
  }
  /** 1.0 = identity, >1 = more vibrant, <1 = desaturated. */
  setSaturation(v: number): this {
    this.saturation = Math.max(0, v);
    return this;
  }

  /** Convenience presets tuned for each context. */
  applyPreset(preset: CinematicPreset): this {
    switch (preset) {
      case 'gameplay':
        return this.setBloom(0.45, 0.78).setVignette(0.28).setChromatic(0.3).setGrain(0.02).setColorGrade(0.08, 0.05).setSaturation(1.2);
      case 'boss':
        return this.setBloom(0.6, 0.72).setVignette(0.4).setChromatic(0.7).setGrain(0.035).setColorGrade(0.14, 0.08).setSaturation(1.25);
      case 'menu':
        return this.setBloom(0.35, 0.8).setVignette(0.22).setChromatic(0.15).setGrain(0.015).setColorGrade(0.06, 0.04).setSaturation(1.15);
      case 'stageClear':
        return this.setBloom(0.75, 0.6).setVignette(0.2).setChromatic(0.25).setGrain(0.015).setColorGrade(0.18, 0.04).setSaturation(1.3);
      case 'gameOver':
        return this.setBloom(0.28, 0.78).setVignette(0.5).setChromatic(0.55).setGrain(0.045).setColorGrade(0.05, 0.22).setSaturation(0.78);
    }
    return this;
  }
}
